import { FC } from 'react';
import { X, Video, Check, XCircle } from 'lucide-react';
import { type CourseHearing } from '@teachur/http-client';
import { Button } from '@new/common/ui/Button/Button';
import { getLatestHearingAttempt } from '../../utils/courseHearingHelpers';

interface CourseHearingDrawerHeaderProps {
  call: CourseHearing | null;
  onClose: () => void;
  onJoinCall: () => void;
  onAccept: () => void;
  onCancel: () => void;
  isAccepting: boolean;
  isCancelling: boolean;
  onOpenSubmissionsHistory?: () => void;
}

export const CourseHearingDrawerHeader: FC<CourseHearingDrawerHeaderProps> = ({
  call,
  onClose,
  onJoinCall,
  onAccept,
  onCancel,
  isAccepting,
  isCancelling,
  onOpenSubmissionsHistory,
}) => {
  if (!call) return null;

  const studentName =
    `${call.student?.firstName ?? ''} ${call.student?.lastName ?? ''}`.trim() || 'Unknown student';
  const courseName = call.course?.name ?? 'Unknown course';
  const attempt = getLatestHearingAttempt(call);
  const isPending = attempt?.status === 'pending';
  const isAccepted = attempt?.status === 'accepted';
  const hasVideoCall = !!attempt?.videoCallId;
  const canShowJoinButton = isAccepted && hasVideoCall;

  return (
    <div className="bg-(--primary-O1000) px-4 py-4 text-(--neutral-white-NW1000)">
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4">
          <h4 className="m-0! [font-size:var(--fs-p2)]! font-semibold text-(--neutral-white-NW1000)!">
            Course Review
          </h4>

          <button
            type="button"
            onClick={onClose}
            className="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-(--neutral-white-NW1000)"
            aria-label="Close drawer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="m-0! [font-size:var(--fs-l4)]! font-semibold text-(--neutral-white-NW1000)!">
                {studentName}
              </p>

              <p className="m-0! [font-size:var(--fs-l5)]! text-(--neutral-white-NW1000)!">
                {courseName}
              </p>
            </div>

            <button
              type="button"
              onClick={onOpenSubmissionsHistory}
              className="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-(--neutral-white-NW1000) [font-size:var(--fs-l5)]! font-medium underline"
              aria-label="View submission history"
            >
              History
            </button>
          </div>

          {isPending && (
            <>
              <p className="m-0! [font-size:var(--fs-p5)]! font-medium! text-(--neutral-white-NW1000)!">
                This course hearing is pending. You can choose to accept it.
              </p>

              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={onAccept}
                  disabled={isAccepting || isCancelling}
                  className="h-9! w-36.5! cursor-pointer rounded-md! border! border-(--primary-O600)! bg-linear-to-r from-(--primary-O800) to-(--primary-O900) px-2! py-3! gap-2"
                >
                  <Check className="w-4 h-4" />
                  <span>Accept</span>
                </Button>
              </div>
            </>
          )}

          {isAccepted && (
            <div className="flex gap-2">
              {canShowJoinButton && (
                <Button variant="secondary" onClick={onJoinCall} disabled={isCancelling}>
                  <Video className="w-4 h-4" />
                  <span>Join Call</span>
                </Button>
              )}
              <Button
                variant="outline"
                onClick={onCancel}
                disabled={isCancelling}
                className="border-(--neutral-white-NW1000)! text-(--neutral-white-NW1000)!"
              >
                <XCircle className="w-4 h-4" />
                <span>Cancel Call</span>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
